import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { encodeOAuthState } from '@modules/integrationsV2/utils/encodeOAuthState';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Link, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { getInstructionsUrlForChannel } from 'modules/ui/Settings/Integrations/utils/getInstructionsUrlForChannel';
import { useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

const getQualtricsBaseURL = (dataCenter: string) =>
  `https://${dataCenter}.qualtrics.com/oauth2/auth`;

type QualtricsFormData = {
  clientID: string;
  clientSecret: string;
  dataCenter: string;
};

const initialState: QualtricsFormData = {
  clientID: '',
  clientSecret: '',
  dataCenter: '',
};

export const QualtricsFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const [formData, setFormData] = useState<QualtricsFormData>(initialState);

  const handleOnChange = <T extends { name: string; value: string }>(
    eventTaget: T,
  ) => {
    const { name, value } = eventTaget;

    setFormData({
      ...formData,
      [name]: name === 'dataCenter' ? value.trim() : value,
    });
  };

  const isDisabled =
    !formData.clientID || !formData.clientSecret || !formData.dataCenter;

  const isReauthorizeForm = !!integrationID;

  const onSubmit = () => {
    if (isDisabled) {
      return;
    }

    const queryParams = {
      response_type: 'code',
      scope: 'read:survey_responses read:surveys',
      redirect_uri: `${window.location.origin}/settings/integrations`,
      client_id: formData.clientID,
      state: encodeOAuthState({
        channelType: IntegrationChannelType.Qualtrics,
        direction: IntegrationDirection.Inbound,
        data: {
          tenantSlug,
          clientID: formData.clientID,
          clientSecret: formData.clientSecret,
          dataCenter: formData.dataCenter,
        },
        integrationType,
        integrationID,
      }),
    };

    const redirectURL = new URL(getQualtricsBaseURL(formData.dataCenter));
    redirectURL.search = new URLSearchParams(queryParams).toString();

    const windowURL = window.open(redirectURL, '_self');

    if (windowURL) {
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Qualtrics,
      });
    }
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <TextField
            required
            label="Qualtrics Client ID"
            placeholder="Enter Client ID"
            onChange={(event) => handleOnChange(event.target)}
            name="clientID"
            value={formData.clientID}
          />
          <TextField
            required
            label="Qualtrics Client Secret"
            placeholder="Enter Cient Secret"
            onChange={(event) => handleOnChange(event.target)}
            name="clientSecret"
            value={formData.clientSecret}
          />
          <TextField
            required
            label="Qualtrics Data Center ID"
            placeholder="Enter Qualtrics Data Center ID"
            onChange={(event) => handleOnChange(event.target)}
            name="dataCenter"
            value={formData.dataCenter}
            helperText={
              <Link
                href={getInstructionsUrlForChannel(integrationChannel)}
                target="_blank"
                variant="caption"
                underline="hover"
              >
                How to locate my Data Center ID?
              </Link>
            }
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={onSubmit}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={false}
      />
    </Stack>
  );
};
